import { InputProps } from '@mui/material/Input';
import InputAdornment from '@mui/material/InputAdornment';
import MuiTextField, {
  TextFieldProps as MuiTextFieldProps,
} from '@mui/material/TextField';
import { ReactNode } from 'react';
import { IconButton } from '../controls';
import { Tooltip } from '../display';
import { SxProps } from '../utils';
import classes from './TextField.module.scss';

export default function TextField({
  clear,
  clearSx,
  disabled,
  disabledTooltip,
  error,
  errorText,
  startAdornment,
  endAdornment,
  name,
  onChange,
  sx,
  value,
  variant = 'standard',
  ...rest
}: TextFieldProps) {
  const helperText = errorText || rest.helperText;
  const inputProps: InputProps = rest.InputProps || ({} as any);
  const adornmentClass = disabled ? classes.disabled : classes.adornment;

  if (startAdornment) {
    inputProps.startAdornment = (
      <InputAdornment className={adornmentClass} position="start">
        {startAdornment}
      </InputAdornment>
    );
  }

  if (clear && value) {
    inputProps.endAdornment = (
      <InputAdornment position="end" sx={clearSx}>
        <IconButton
          buttonSize="small"
          color="default"
          icon="times"
          onClick={(e) =>
            onChange?.({
              ...e,
              target: { ...e.target, name, value: '' },
            } as any)
          }
          size="small"
          sx={{
            'opacity': 0,
            'px': 1,
            'transition': 'opacity .3s',
            '.MuiInput-root:hover &': {
              opacity: 1,
            },
          }}
          title="Clear"
        />
      </InputAdornment>
    );
  }

  if (endAdornment) {
    inputProps.endAdornment = (
      <InputAdornment className={adornmentClass} position="end">
        {endAdornment}
      </InputAdornment>
    );
  }

  const tooltipText = disabled && disabledTooltip;

  return (
    <Tooltip title={tooltipText}>
      <MuiTextField
        {...rest}
        disabled={disabled}
        error={error || Boolean(errorText)}
        helperText={helperText}
        InputProps={inputProps}
        name={name}
        onChange={onChange}
        sx={{ mt: 2, width: '100%', ...sx }}
        value={value}
        variant={variant}
      />
    </Tooltip>
  );
}

export type TextFieldProps = {
  clear?: boolean;
  clearSx?: SxProps;
  disabled?: boolean;
  disabledTooltip?: string;
  endAdornment?: ReactNode;
  errorText?: ReactNode;
  startAdornment?: ReactNode;
} & MuiTextFieldProps;
